// Context-free marketing fleet-card "from" price label (SSR/no-JS fallback).
//
// A generic fleet card has NO route context, and `aircraft.price` is unused (null
// for every aircraft — pricing is route/operator-based via the candidate-pricing
// engine), so there is no accurate context-free "from" price to publish. Render a
// real "Price from EUR X." ONLY when a positive numeric price is genuinely present;
// otherwise render the clean "Price on request." wording. Never an empty "EUR .",
// "EUR 0", or "EUR NaN". Display-only, read-only — no money value is computed here.
export function fleetCardPriceLabel(price) {
  const n = Number(price);
  return Number.isFinite(n) && n > 0
    ? `Price from EUR ${n.toLocaleString('de-DE')}.`
    : 'Price on request.';
}

// Inline "from EUR X" label for the ROUTE-page aircraft cards + route hero. The
// price MUST be the canonical route.cardFromPrice (the cheapest customer total over
// all valid constellations) — passed in read-only. A missing/zero/NaN value always
// falls back to "Price on request", never an empty/zero "EUR ". de-DE formatted,
// same rounding the funnel uses (whole EUR via customerBaseTotal).
export function routeCardFromLabel(price) {
  const n = Number(price);
  return Number.isFinite(n) && n > 0
    ? `from EUR ${n.toLocaleString('de-DE')}`
    : 'Price on request';
}

// Route overall cheapest customer total (NUMBER, or null) across all aircraft on the
// route, from the precomputed route.cardFromPrice map. The min is taken over ONLY the
// positive finite per-aircraft values, so an empty/all-null map yields null (NOT
// Math.min([]) = Infinity). Single source for the hero, stats-strip, cards and Key
// Facts so they never disagree. Read-only — no price is computed here.
export function routeFromPriceMin(cardFromPrice) {
  const vals = Object.values(cardFromPrice || {}).filter(v => typeof v === 'number' && Number.isFinite(v) && v > 0);
  return vals.length ? Math.min(...vals) : null;
}

// Route overall "from EUR X" label (hero bullet / aircraft-list contexts where there
// is NO "from" in a nearby label). null/empty → "Price on request"; never "∞" / "EUR .".
export function routeFromPriceMinLabel(cardFromPrice) {
  return routeCardFromLabel(routeFromPriceMin(cardFromPrice));
}

// AMOUNT-ONLY "EUR X" (de-DE), no "from " prefix — for a stats-strip cell whose LABEL
// is already "From price" (avoids the double "from EUR X under FROM PRICE"). When
// there is no positive min it still degrades to "Price on request", never an empty
// "EUR ." or "∞".
export function routeFromPriceAmountLabel(cardFromPrice) {
  return priceAmountLabel(routeFromPriceMin(cardFromPrice));
}

// AMOUNT-only "EUR X" (de-DE) from an already-resolved NUMBER (or null). Like
// routeFromPriceAmountLabel but for a min already computed (e.g. a destination's
// from-Athens min). null/non-positive → "Price on request", never "EUR ." / "∞".
export function priceAmountLabel(n) {
  return (typeof n === 'number' && Number.isFinite(n) && n > 0) ? `EUR ${n.toLocaleString('de-DE')}` : 'Price on request';
}

// Label-aware Key-Facts / stats price VALUE for a resolved numeric min. When the row
// LABEL already contains the word "from" (e.g. "From price", "Price from", "Starting
// from") the value is amount-only "EUR X" so the pair never reads "From price: from
// EUR X"; otherwise (e.g. "Starting Price", "Price") it is "from EUR X". Empty min →
// "Price on request" either way. Display-only.
export function priceValueForLabel(label, min) {
  return /\bfrom\b/i.test(String(label || '')) ? priceAmountLabel(min) : routeCardFromLabel(min);
}

// Overall cheapest from-price (NUMBER, or null) across a SET of routes — each via its
// own cardFromPrice min. Used to derive a destination card "from EUR X" as the min of
// every Athens->destination route (a destination has no single cardFromPrice).
// Read-only; never recomputes a price.
export function routesFromPriceMin(routes) {
  let min = null;
  for (const r of (routes || [])) {
    const m = routeFromPriceMin(r && r.cardFromPrice);
    if (m != null && (min == null || m < min)) min = m;
  }
  return min;
}

// Identify the route/destination Key Facts PRICE row. Its stored CMS value is stale/
// legacy (e.g. the "From EUR ∞" Math.min-over-empty artifact, or "On request"), so the
// display overrides it with the live route/dest min. Match is on the LABEL only
// (case/space-insensitive), never the value. The generator is inconsistent — routes
// say "Starting Price" or just "Price"; destinations say "Pricing" — so we treat ANY
// label containing "pric" (price / pricing / starting price / from price / price from)
// OR the "starting from" variant as the price row. Robust to future label wording.
export function isStartingPriceKeyFact(label) {
  if (typeof label !== 'string') return false;
  const l = label.trim().toLowerCase();
  return /pric/.test(l) || l === 'starting from';
}

// Dedup the route-page aircraft cards by (aircraft MODEL/name x serving OPERATOR),
// mirroring the alternatives-page rule: the customer never sees the same model from
// the same operator twice; two DIFFERENT operators on the same model stay as
// separate cards (operator competition). Display/grouping only — no price math.
//
// Per group:
//   - fromPrice = the MIN route.cardFromPrice across ALL instances in the group,
//     INCLUDING hideFromFleetPage instances (owner-approved: a hidden aircraft's
//     price may drive the from-price, so it matches the route hero). null when no
//     instance has an eligible price (→ "Price on request" via routeCardFromLabel).
//   - rep = a VISIBLE (non-hidden) instance — the cheapest-priced one — used for the
//     card image/name/specs/link. A hidden instance is NEVER rendered as its own card.
//   - groups whose every instance is hidden render NO card at all.
//
// The serving/pricing operator per aircraft comes from the precomputed
// route.cardTotals[] (operatorId per offered (operator, aircraft)); read-only, no
// engine call. minInstanceId records which instance gave the min (incl. hidden) for
// verification. Sorted cheapest-first.
export function dedupeRouteCards(route, fleet) {
  const cfp = (route && route.cardFromPrice) || {};
  // serving/pricing operator per aircraft (from the precomputed cardTotals).
  const opOf = {};
  for (const c of ((route && route.cardTotals) || [])) {
    if (c && c.aircraft && c.operatorId && opOf[c.aircraft] == null) opOf[c.aircraft] = c.operatorId;
  }
  const groups = new Map(); // key = name   operatorId
  for (const f of (fleet || [])) {
    if (!f || !f.id || !f.name) continue;
    const raw = Number(cfp[f.id]);
    const price = (Number.isFinite(raw) && raw > 0) ? raw : null;
    const opId = opOf[f.id] || '';
    const key = f.name + ' ' + opId;
    let g = groups.get(key);
    if (!g) { g = { model: f.name, operatorId: opId || null, fromPrice: null, minInstanceId: null, rep: null, instanceIds: [], _repPrice: Infinity }; groups.set(key, g); }
    g.instanceIds.push(f.id);
    if (price != null && (g.fromPrice == null || price < g.fromPrice)) { g.fromPrice = price; g.minInstanceId = f.id; }
    if (f.hideFromFleetPage !== true) {
      const vp = price != null ? price : Infinity;
      if (!g.rep || vp < g._repPrice) { g.rep = f; g._repPrice = vp; }
    }
  }
  // only groups with a VISIBLE representative render a card (all-hidden group → none).
  return [...groups.values()]
    .filter(g => g.rep)
    .map(({ _repPrice, ...g }) => g)
    .sort((a, b) => (a.fromPrice == null ? Infinity : a.fromPrice) - (b.fromPrice == null ? Infinity : b.fromPrice)
      || a.model.localeCompare(b.model));
}
